import type { ShopSchedule } from '@/api/types/shop.types';

const BAKU_TZ = 'Asia/Baku';
const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export interface TodayScheduleStatus {
  isOpen: boolean;
  label: string;
  statusLabel: string;
  timeLabel: string;
  nextTime?: string;
}

export interface ScheduleDayItem {
  dayName: string;
  hours: string;
}

function getNowInBaku(): {
  dayOfWeek: string;
  minutesSinceMidnight: number;
} {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: BAKU_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'long',
  });
  const parts = formatter.formatToParts(now);
  let hour = 0;
  let minute = 0;
  let weekday = '';
  for (const p of parts) {
    if (p.type === 'hour') {
      hour = parseInt(p.value, 10) || 0;
      if (hour === 24) hour = 0;
    }
    if (p.type === 'minute') minute = parseInt(p.value, 10) || 0;
    if (p.type === 'weekday') weekday = p.value;
  }
  return {
    dayOfWeek: weekday,
    minutesSinceMidnight: hour * 60 + minute,
  };
}

function parseTimeParts(timeStr: string): [number, number] {
  const normalized = (timeStr || '').trim().replace(/-/g, ':');
  const [h, m] = normalized
    .split(':')
    .map(s => parseInt(s?.trim() ?? '0', 10) || 0);
  return [h, m];
}

function parseTimeToMinutes(timeStr: string): number {
  const [h, m] = parseTimeParts(timeStr);
  return h * 60 + m;
}

function closeTimeToMinutes(timeStr: string): number {
  const mins = parseTimeToMinutes(timeStr);
  return mins === 0 ? 24 * 60 : mins;
}

function formatTime(timeStr: string): string {
  const [h, m] = parseTimeParts(timeStr);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function isSchedule24x7(entry: ShopSchedule): boolean {
  if (!entry?.checked || !entry?.openTime?.trim() || !entry?.closeTime?.trim())
    return false;
  const open = entry.openTime.trim();
  const close = entry.closeTime.trim();
  return open === close && open === '00:00';
}

function findScheduleDay(
  schedule: ShopSchedule[],
  dayOfWeek: string
): ShopSchedule | undefined {
  const key = dayOfWeek.toLowerCase();
  return schedule.find(entry => entry.dayOfWeek.toLowerCase() === key);
}

function weekdayIndex(dayOfWeek: string): number {
  return WEEKDAYS.findIndex(day => day === dayOfWeek.toLowerCase());
}

function previousWeekday(dayOfWeek: string): string {
  const index = weekdayIndex(dayOfWeek);
  if (index < 0) return '';
  return WEEKDAYS[(index + 6) % 7];
}

function getNextOpeningFromSchedule(
  schedule: ShopSchedule[],
  afterDay: string
): { time: string; dayOfWeek: string; is24_7: boolean } | null {
  const start = weekdayIndex(afterDay);
  if (start < 0) return null;

  for (let step = 1; step <= WEEKDAYS.length; step++) {
    const entry = findScheduleDay(schedule, WEEKDAYS[(start + step) % 7]);
    if (entry?.checked && entry.openTime?.trim()) {
      return {
        time: formatTime(entry.openTime),
        dayOfWeek: entry.dayOfWeek,
        is24_7: isSchedule24x7(entry),
      };
    }
  }
  return null;
}

type ScheduleT = (key: string, options?: Record<string, string>) => string;

function buildStatus(
  t: ScheduleT,
  isOpen: boolean,
  opts: { timeLabel?: string; nextTime?: string }
): TodayScheduleStatus {
  const statusLabel = t(isOpen ? 'shop.schedule_open' : 'shop.schedule_closed');
  const timeLabel = opts.timeLabel ?? '';
  return {
    isOpen,
    label: timeLabel ? `${statusLabel} ${timeLabel}` : statusLabel,
    statusLabel,
    timeLabel,
    nextTime: opts.nextTime,
  };
}

function getShortDayLabel(dayOfWeek: string, t: ScheduleT): string {
  return t(`shop.schedule_short_${dayOfWeek.toLowerCase()}`);
}

function isValidScheduleEntry(
  entry: ShopSchedule | undefined
): entry is ShopSchedule {
  return Boolean(
    entry?.checked && entry.openTime?.trim() && entry.closeTime?.trim()
  );
}

function isOvernightEntry(entry: ShopSchedule): boolean {
  if (isSchedule24x7(entry)) return false;
  return (
    closeTimeToMinutes(entry.closeTime) <= parseTimeToMinutes(entry.openTime)
  );
}

type ScheduleSpan = { open: number; close: number; closeTime: string };

type OpenCloseTime = {
  closeTime: string;
  continuous: boolean;
  closeDay?: string;
};

function entrySpan(entry: ShopSchedule, dayOffset: number): ScheduleSpan {
  const open = dayOffset * 24 * 60 + parseTimeToMinutes(entry.openTime);
  if (isSchedule24x7(entry)) {
    return { open, close: open + 24 * 60, closeTime: entry.closeTime };
  }
  let close = dayOffset * 24 * 60 + closeTimeToMinutes(entry.closeTime);
  if (isOvernightEntry(entry)) close += 24 * 60;
  return { open, close, closeTime: entry.closeTime };
}

function buildScheduleSpans(
  schedule: ShopSchedule[],
  options: {
    weekdayStart: number;
    today?: ShopSchedule;
    yesterday?: ShopSchedule;
  }
): ScheduleSpan[] {
  const { weekdayStart, today, yesterday } = options;
  const spans: ScheduleSpan[] = [];
  if (isValidScheduleEntry(yesterday)) spans.push(entrySpan(yesterday, -1));
  if (isValidScheduleEntry(today)) spans.push(entrySpan(today, 0));

  for (let dayOffset = 1; dayOffset < WEEKDAYS.length; dayOffset += 1) {
    const entry = findScheduleDay(
      schedule,
      WEEKDAYS[(weekdayStart + dayOffset) % WEEKDAYS.length]
    );
    if (isValidScheduleEntry(entry)) spans.push(entrySpan(entry, dayOffset));
  }
  return spans;
}

function connectedCloseMinutes(
  spans: ScheduleSpan[],
  minutesSinceMidnight: number
): number {
  let close = spans
    .filter(
      span =>
        span.open <= minutesSinceMidnight && minutesSinceMidnight < span.close
    )
    .reduce((latest, span) => Math.max(latest, span.close), -1);
  if (close < 0) return close;

  for (const span of spans) {
    if (span.open <= close && span.close > close) close = span.close;
  }
  return close;
}

function closeWeekday(weekdayStart: number, close: number): string | undefined {
  const closeDayOffset = Math.floor(close / (24 * 60));
  if (closeDayOffset === 0) return undefined;
  return WEEKDAYS[
    (weekdayStart + closeDayOffset + WEEKDAYS.length) % WEEKDAYS.length
  ];
}

function resolveConnectedInterval(
  spans: ScheduleSpan[],
  options: {
    today?: ShopSchedule;
    minutesSinceMidnight: number;
    weekdayStart: number;
  }
): OpenCloseTime | null {
  const { today, minutesSinceMidnight, weekdayStart } = options;
  const close = connectedCloseMinutes(spans, minutesSinceMidnight);
  if (close < 0) return null;

  if (
    isValidScheduleEntry(today) &&
    entrySpan(today, WEEKDAYS.length).open <= close
  ) {
    return { closeTime: today.closeTime, continuous: true };
  }

  const closeTime = spans.find(span => span.close === close)?.closeTime;
  if (!closeTime) return null;
  return {
    closeTime,
    continuous: false,
    closeDay: closeWeekday(weekdayStart, close),
  };
}

function resolveOpenCloseTime(
  schedule: ShopSchedule[],
  options: {
    dayOfWeek: string;
    today?: ShopSchedule;
    yesterday?: ShopSchedule;
    minutesSinceMidnight: number;
  }
): OpenCloseTime | null {
  const { dayOfWeek, today, yesterday, minutesSinceMidnight } = options;
  const weekdayStart = weekdayIndex(dayOfWeek);
  if (weekdayStart < 0) return null;

  const spans = buildScheduleSpans(schedule, {
    weekdayStart,
    today,
    yesterday,
  });
  return resolveConnectedInterval(spans, {
    today,
    minutesSinceMidnight,
    weekdayStart,
  });
}

function buildClosedUntilNext(
  schedule: ShopSchedule[],
  options: {
    afterDay: string;
    t: ScheduleT;
    allWeek24x7: boolean;
  }
): TodayScheduleStatus {
  const { afterDay, t, allWeek24x7 } = options;
  const nextOpen = getNextOpeningFromSchedule(schedule, afterDay);
  if (!nextOpen) return buildStatus(t, false, {});
  if (nextOpen.is24_7 && allWeek24x7) {
    return buildStatus(t, false, {
      timeLabel: t('shop.schedule_until', {
        time: t('shop.schedule_24_7'),
      }),
      nextTime: nextOpen.time,
    });
  }
  return buildStatus(t, false, {
    timeLabel: t('shop.schedule_until_day', {
      time: nextOpen.time,
      day: getShortDayLabel(nextOpen.dayOfWeek, t),
    }),
    nextTime: nextOpen.time,
  });
}

function openUntilTimeLabel(
  t: ScheduleT,
  options: {
    time: string;
    closeDay?: string;
    aroundTheClockLabel?: string;
  }
): string {
  const { time, closeDay, aroundTheClockLabel } = options;
  if (aroundTheClockLabel) return aroundTheClockLabel;
  if (!closeDay) return t('shop.schedule_until', { time });
  return t('shop.schedule_until_day', {
    time,
    day: getShortDayLabel(closeDay, t),
  });
}

export function getTodayScheduleStatusFromSchedule(
  schedule: ShopSchedule[],
  t: ScheduleT,
  now = getNowInBaku()
): TodayScheduleStatus | null {
  if (!schedule?.length) return null;

  const allWeek24x7 = isScheduleAll24x7(schedule);
  const { dayOfWeek, minutesSinceMidnight } = now;
  const todayIndex = schedule.findIndex(
    s => s.dayOfWeek.toLowerCase() === dayOfWeek.toLowerCase()
  );
  const today = todayIndex >= 0 ? schedule[todayIndex] : undefined;
  const yesterday = findScheduleDay(schedule, previousWeekday(dayOfWeek));
  const openUntil = resolveOpenCloseTime(schedule, {
    dayOfWeek,
    today,
    yesterday,
    minutesSinceMidnight,
  });
  const today24x7 = Boolean(today && isSchedule24x7(today));
  const timeLabel24x7 = t('shop.schedule_24_7');

  if (openUntil) {
    const showAroundTheClock =
      (today24x7 && allWeek24x7) || openUntil.continuous;
    const time = formatTime(openUntil.closeTime);
    const timeLabel = openUntilTimeLabel(t, {
      time,
      closeDay: openUntil.closeDay,
      aroundTheClockLabel: showAroundTheClock ? timeLabel24x7 : undefined,
    });
    return buildStatus(t, true, {
      timeLabel,
      nextTime: showAroundTheClock
        ? undefined
        : formatTime(openUntil.closeTime),
    });
  }

  if (
    isValidScheduleEntry(today) &&
    minutesSinceMidnight < parseTimeToMinutes(today.openTime)
  ) {
    const timeLabel =
      today24x7 && allWeek24x7
        ? timeLabel24x7
        : t('shop.schedule_until_day', {
            time: formatTime(today.openTime),
            day: getShortDayLabel(today.dayOfWeek, t),
          });
    return buildStatus(t, false, {
      timeLabel,
      nextTime:
        today24x7 && allWeek24x7 ? undefined : formatTime(today.openTime),
    });
  }

  return buildClosedUntilNext(schedule, {
    afterDay: dayOfWeek,
    t,
    allWeek24x7,
  });
}

export function isScheduleAll24x7(schedule: ShopSchedule[]): boolean {
  if (!schedule?.length || schedule.length < 7) return false;
  return schedule.every(
    e =>
      e.checked &&
      e.openTime?.trim() &&
      e.closeTime?.trim() &&
      isSchedule24x7(e)
  );
}

export function getScheduleDaysListFromSchedule(
  schedule: ShopSchedule[],
  t: (key: string) => string
): ScheduleDayItem[] {
  if (!schedule?.length) return [];

  const closedLabel = t('shop.schedule_closed');

  return schedule.map((entry: ShopSchedule) => {
    const dayName = t(`shop.schedule_${entry.dayOfWeek.toLowerCase()}`);
    const hours =
      entry.checked && entry.openTime?.trim() && entry.closeTime?.trim()
        ? `${formatTime(entry.openTime)} – ${formatTime(entry.closeTime)}`
        : closedLabel;
    return { dayName, hours };
  });
}
